import { FC, useEffect, useState } from 'react';
import trashIcon from '../../assets/icons/thrashSvg.svg';
import { networkIconUrl } from '../../assets/icons/networks';
import { translate } from '../../i18n';
import { theme } from '../../theme';
import { getButtonStyle, paymentMethodCardStyles } from './styles';

export interface PaymentMethodCardProps {
  issuerName: string;
  last4: string;
  holderName: string;
  /** Vencimiento MM/AA. Solo en el alta, como en el flujo Web. */
  expiry?: string;
  /** Número completo agrupado. Si viene, reemplaza la máscara ****. */
  pan?: string;
  network: string;
  networkVariant: string;
  /** Clave del catálogo (visa, mastercard, amex, cabal, maestro, diners, naranja). */
  icon?: string;
  gradientColors?: [string, string];
  textColor?: string;
  isSelected?: boolean;
  /** En pagar con 2 tarjetas: una sola línea y menos datos. */
  compact?: boolean;
  onPress?: () => void;
  onRemove?: () => void;
  testID?: string;
  accessibilityLabel?: string;
}

/**
 * PaymentMethodCard — Tarjeta de método de pago con gradiente dinámico
 *
 * Cambios:
 * ✅ Estilos centralizados en styles.ts
 * ✅ Gradient dinámico soportado via getButtonStyle()
 * ✅ i18n para aria-label
 * ✅ Cero Tailwind, cero hardcodes
 */
export const PaymentMethodCard: FC<PaymentMethodCardProps> = ({
  issuerName,
  last4,
  holderName,
  expiry,
  pan,
  network,
  networkVariant,
  icon,
  gradientColors = ['#DDA056', '#FF8000'],
  textColor = theme.colors.white,
  isSelected = false,
  compact = false,
  onPress,
  onRemove,
  testID = 'payment-method-card',
  accessibilityLabel = translate('paymentsUI.paymentMethodCard.ariaLabel', {
    issuerName,
    last4,
  }),
}) => {
  const iconUrl = networkIconUrl(icon, network);
  const [iconFailed, setIconFailed] = useState(false);
  const inkIsWhite = textColor.trim().toLowerCase() === '#ffffff' || textColor.trim().toLowerCase() === '#fff';

  useEffect(() => {
    setIconFailed(false);
  }, [iconUrl]);

  const mark = iconUrl && !iconFailed ? (
    <img
      src={iconUrl}
      alt={network}
      data-testid={`${testID}-mark`}
      onError={() => setIconFailed(true)}
      style={{
        ...paymentMethodCardStyles.networkMark,
        filter: inkIsWhite ? undefined : 'brightness(0)',
      }}
    />
  ) : (
    <div style={{ ...paymentMethodCardStyles.networkName, color: textColor }}>{network}</div>
  );

  return (
    <div style={paymentMethodCardStyles.shell}>
    <button
      onClick={onPress}
      disabled={!onPress}
      data-testid={testID}
      aria-label={accessibilityLabel}
      role="button"
      style={getButtonStyle(gradientColors, isSelected, compact)}
    >
      {compact ? (
        <>
          {mark}
          <span style={{ ...paymentMethodCardStyles.compactLine, color: textColor }}>
            {issuerName} · **** {last4}
          </span>
        </>
      ) : (
      <>
      {/* Issuer name */}
      <div
        style={{
          ...paymentMethodCardStyles.issuerName,
          color: textColor,
          paddingRight: onRemove ? 24 : 0,
        }}
      >
        {issuerName}
      </div>

      {/* Card details frame */}
      <div style={paymentMethodCardStyles.cardDetailsFrame}>
        {/* Left content - card number and holder */}
        <div style={paymentMethodCardStyles.leftContent}>
          {expiry ? (
            <div style={{ ...paymentMethodCardStyles.cardNumber, color: textColor }}>{expiry}</div>
          ) : null}
          <div style={{ ...paymentMethodCardStyles.cardNumber, color: textColor }}>
            {pan || `**** ${last4}`}
          </div>
          <div style={{ ...paymentMethodCardStyles.holderName, color: textColor }}>{holderName}</div>
        </div>

        {/* Right content - network info */}
        <div style={paymentMethodCardStyles.rightContent}>
          {mark}
          <div style={{ ...paymentMethodCardStyles.networkVariant, color: textColor }}>{networkVariant}</div>
        </div>
      </div>
      </>
      )}
    </button>
    {onRemove ? (
      <button
        type="button"
        style={paymentMethodCardStyles.removeButton}
        aria-label={translate('paymentsUI.web.deleteCard')}
        data-testid={`${testID}-remove`}
        onClick={onRemove}
      >
        <img
          src={trashIcon}
          alt=""
          width={13}
          height={15}
          style={{ filter: inkIsWhite ? 'brightness(0) invert(1)' : 'brightness(0)' }}
        />
      </button>
    ) : null}
    </div>
  );
};
